import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  TooltipProps,
  LegendProps,
} from 'recharts';
import { RealtimeStatItem } from '../../../types/naver.types';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';

type Props<T extends RealtimeStatItem> = {
  title: string;
  data: T[];
  valueKey: Extract<keyof T, string>;
};

const COLORS = ['#0044D2', '#00A389', '#F97316', '#8B5CF6', '#EC4899', '#EAB308', '#14B8A6', '#EF4444'];

const formatTime = (value: string) => value.slice(5, 16).replace('T', ' ');

export default function RealtimeLineChart<T extends RealtimeStatItem>({ title, data, valueKey }: Props<T>) {
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [isolatedSeries, setIsolatedSeries] = useState<string | null>(null);

  const handleLegendClick: LegendProps['onClick'] = (entry) => {
    const key = entry.dataKey as string;
    setIsolatedSeries((prev) => (prev === key ? null : key));
  };

  const appNames = useMemo(() => {
    const seen = new Set<string>();
    data.forEach((row) => seen.add(row.appName));
    return Array.from(seen).sort();
  }, [data]);

  const chartData = useMemo(() => {
    const map = new Map<string, Record<string, string | number>>();
    data.forEach((row) => {
      let entry = map.get(row.dateTime);
      if (!entry) {
        entry = { dateTime: row.dateTime };
        map.set(row.dateTime, entry);
      }
      entry[row.appName] = row[valueKey] as number;
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.dateTime as string).localeCompare(b.dateTime as string)
    );
  }, [data, valueKey]);

  // 호버 중인 라인의 데이터만 보여주는 커스텀 툴팁
  const renderTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload || !hoveredSeries) return null;
    const entry = payload.find((p) => p.dataKey === hoveredSeries);
    if (!entry) return null;
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, p: 1.2, boxShadow: 1 }}>
        <Typography fontSize="0.72rem" color="#94a3b8">
          {formatTime(label)}
        </Typography>
        <Typography fontSize="0.85rem" fontWeight={700} sx={{ color: entry.color }}>
          {entry.name}: {entry.value?.toLocaleString('ko-KR')}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        ...PAGE_CARD_SX,
        mt: 0,
      }}
    >
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="dateTime" tickFormatter={formatTime} minTickGap={40} />
          <YAxis allowDecimals={false} />
          <RechartsTooltip content={renderTooltip} />
          <Legend
            onClick={handleLegendClick}
            wrapperStyle={{ cursor: 'pointer' }}
            formatter={(value) => (
              <span style={{ opacity: isolatedSeries && isolatedSeries !== value ? 0.35 : 1 }}>{value}</span>
            )}
          />
          {appNames.map((appName, i) => (
            <Line
              key={appName}
              type="monotone"
              dataKey={appName}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              strokeOpacity={!hoveredSeries || hoveredSeries === appName ? 1 : 0.15}
              dot={false}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
              hide={isolatedSeries !== null && isolatedSeries !== appName}
            />
          ))}
          {/* 얇은 라인은 정확히 호버하기 어려워서, 보이지 않는 두꺼운 라인을 위에 겹쳐 히트 영역으로 사용 */}
          {appNames.map((appName) => (
            <Line
              key={`${appName}-hit`}
              type="monotone"
              dataKey={appName}
              stroke="transparent"
              strokeWidth={20}
              dot={false}
              activeDot={false}
              legendType="none"
              connectNulls
              isAnimationActive={false}
              hide={isolatedSeries !== null && isolatedSeries !== appName}
              onMouseEnter={() => setHoveredSeries(appName)}
              onMouseLeave={() => setHoveredSeries(null)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
